import {
  useApolloClient,
  useMutation,
  useQuery,
  useSuspenseQuery,
} from "@apollo/client";
import {
  Alert,
  Button,
  CurrencyAmountUI,
  Input,
  Label,
  LabelPill,
  Number as NumberComponent,
  PrimaryFullScreenSpinner,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Spinner,
  toast,
  useAppForm,
} from "@repo/ui";
import {
  currencySchema,
  isCurrency,
  NotFoundError,
  neverFunc,
  prettyPrintEnum,
} from "@repo/utils";
import { useStore } from "@tanstack/react-form";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import Decimal from "decimal.js";
import { DateTime } from "luxon";
import { type ReactNode, Suspense, useMemo } from "react";
import { useDebounceValue } from "usehooks-ts";
import { z } from "zod";
import { AdminPageContainer } from "~/components/layout/AdminPageContainer";
import { AdminPageHeader } from "~/components/layout/AdminPageHeader";
import { graphql } from "~/gql/client";
import {
  fXTradeDatePreferenceValues,
  fXTradeOperationValues,
} from "~/gql/client/enumValues";
import type { Currency, FxTradeOperation } from "~/gql/client/graphql";
import { opsHead } from "~/head";

export const Route = createFileRoute("/__auth/fx-trades_/new")({
  head: () => opsHead("New spot FX trade"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

const createFXTradeFormSchema = z.object({
  amount: z.number().transform((v) => new Decimal(v)),
  businessEntityId: z.string(),
  buyCurrency: currencySchema,
  sellCurrency: currencySchema,
  operation: z.enum(fXTradeOperationValues),
  organisationId: z.string(),
  datePreference: z.enum([...fXTradeDatePreferenceValues, "CUSTOM_DATE"]),
  valueDate: z
    .date()
    .transform((d) => DateTime.fromJSDate(d).toISODate())
    .refine((d) => d !== null),
});

function View() {
  // todo: filter orgs by status
  const { data } = useSuspenseQuery(
    graphql(`
      query AdminNewFXTradePage {
        organisations {
          id
          name
          fxMarginRateBps
          businessEntities {
            id
            name
            fxProvider {
              id
            }
            assets {
              id
              class
              currency
            }
          }
        }
      }
    `),
  );
  const organisationOptions = data.organisations;
  if (!organisationOptions) {
    throw new NotFoundError();
  }

  const client = useApolloClient();

  const navigate = useNavigate();

  const filteredOrganisationOptions = organisationOptions
    .map((org) => ({
      ...org,
      businessEntities: org.businessEntities
        ?.filter(
          (be) =>
            be.fxProvider?.__typename === "EburyClient" ||
            be.fxProvider?.__typename === "CurrencycloudAccount",
        )
        .map((be) => {
          const fxProvider: "EBURY" | "CURRENCYCLOUD" =
            be.fxProvider?.__typename === "EburyClient"
              ? "EBURY"
              : "CURRENCYCLOUD";

          return {
            ...be,
            assets: be.assets?.filter((asset) => asset.class === "FX") ?? [],
            fxProvider,
          };
        }),
    }))
    .map((org) => {
      const fxProviders = Array.from(
        new Set(org.businessEntities?.map((be) => be.fxProvider) ?? []),
      );
      return {
        ...org,
        fxProviders,
      };
    })
    .filter((org) => {
      if (!org.businessEntities || org.businessEntities.length === 0) {
        return false;
      }
      return org.fxProviders.length > 0;
    });

  const [createQuote, { loading: quoteLoading, data: quoteData }] = useMutation(
    graphql(`
      mutation CreateFXTradeQuote($input: FXTradeQuoteInput!) {
        createFXTradeQuote(input: $input) {
          quote {
            id
            externalId
            buyAmount {
              amount
              currency
            }
            sellAmount {
              amount
              currency
            }
            buyToSellRate
            sellToBuyRate
            valueDate
            feeAmount {
              amount
              currency
            }
          }
        }
      }
    `),
    {
      onError(error) {
        const errorMessage =
          error.graphQLErrors?.[0]?.message ||
          "Failed to create FX Trade quote";

        toast({
          kind: "error",
          autoHide: false,
          title: "Error creating quote",
          description: errorMessage,
        });
      },
    },
  );

  const [createFXTradeMutation, { loading: tradeLoading }] = useMutation(
    graphql(`
      mutation CreateFXTrade($input: CreateFXTradeInput!) {
        createFXTrade(input: $input) {
          fxTrade {
            id
            businessEntity {
              id
            }
          }
        }
      }
    `),
    {
      onError(error) {
        const errorMessage =
          error.graphQLErrors?.[0]?.message || "Failed to create FX Trade";

        toast({
          kind: "error",
          autoHide: false,
          title: "Error creating FX trade",
          description: errorMessage,
        });
      },
      onCompleted: (data) => {
        // mutation may return successfully without the fxTrade payload
        if (
          data.createFXTrade.fxTrade &&
          data.createFXTrade.fxTrade.businessEntity?.id !== undefined
        ) {
          client.cache.evict({
            id: `BusinessEntity:${data.createFXTrade.fxTrade.businessEntity.id}`,
            fieldName: "fxTrades",
          });
        }

        if (data.createFXTrade.fxTrade) {
          void navigate({
            to: "/fx-trades/$fxTradeId",
            params: { fxTradeId: data.createFXTrade.fxTrade.id },
          });
          return;
        }

        void navigate({
          to: "/fx-trades",
        });
        return;
      },
    },
  );
  const defaultValueDate = useMemo(() => {
    return new Date(new Date().setHours(0, 0, 0, 0));
  }, []);

  const newQuoteForm = useAppForm({
    defaultValues: {
      organisationId: "",
      businessEntityId: "",
      operation: "SELL",
      amount: 0,
      sellCurrency: "",
      buyCurrency: "",
      valueDate: defaultValueDate,
      datePreference: "EARLIEST",
    },
    validators: {
      onSubmit: createFXTradeFormSchema,
    },
    // todo: client side validation doesn't appear to be happening on submit
    onSubmit: async ({ value }) => {
      const parsed = createFXTradeFormSchema.safeParse(value);
      if (!parsed.success) {
        toast({
          kind: "error",
          autoHide: false,
          title: "Error creating quote",
        });
        return;
      }

      return createQuote({
        variables: {
          input: {
            businessEntityId: parsed.data.businessEntityId,
            operation: parsed.data.operation,
            amount: parsed.data.amount,
            buyCurrency: parsed.data.buyCurrency,
            sellCurrency: parsed.data.sellCurrency,
            valueDate: parsed.data.valueDate,
            tradeType: "SPOT",
          },
        },
      });
    },
  });

  const selectedOrganisationId = useStore(
    newQuoteForm.store,
    (state) => state.values.organisationId,
  );
  const selectedBusinessEntityId = useStore(
    newQuoteForm.store,
    (state) => state.values.businessEntityId,
  );
  const selectedBuyCurrency = useStore(newQuoteForm.store, (state) =>
    isCurrency(state.values.buyCurrency) ? state.values.buyCurrency : undefined,
  );
  const selectedSellCurrency = useStore(newQuoteForm.store, (state) =>
    isCurrency(state.values.sellCurrency)
      ? state.values.sellCurrency
      : undefined,
  );
  const selectedAmount = useStore(
    newQuoteForm.store,
    (state) => new Decimal(state.values.amount),
  );
  const selectedDatePreference = useStore(
    newQuoteForm.store,
    (state) => state.values.datePreference,
  );

  const selectedOperation = useStore(newQuoteForm.store, (state) =>
    fXTradeOperationValues.includes(state.values.operation as FxTradeOperation)
      ? (state.values.operation as FxTradeOperation)
      : undefined,
  );
  const selectedOrganisation =
    filteredOrganisationOptions && selectedOrganisationId
      ? filteredOrganisationOptions.find((o) => o.id === selectedOrganisationId)
      : undefined;

  const businessEntityOptions =
    filteredOrganisationOptions &&
    selectedOrganisation &&
    selectedOrganisation.businessEntities
      ? selectedOrganisation.businessEntities
      : [];

  const selectedBusinessEntity = businessEntityOptions.find(
    (be) => be.id === selectedBusinessEntityId,
  );

  const selectedAmountValue = useStore(
    newQuoteForm.store,
    (state) => state.values.amount,
  );
  const [debouncedAmount] = useDebounceValue(selectedAmountValue, 500);
  const isEburyBusinessEntity = selectedBusinessEntity?.fxProvider === "EBURY";
  // Memoised as Apollo's variable equality check treats separate Decimal instances as unequal, so a new Decimal on
  // every render triggers a refetch loop.
  const eburyValueDateOptionsInput = useMemo(() => {
    if (
      !isEburyBusinessEntity ||
      !selectedOperation ||
      !selectedSellCurrency ||
      !selectedBuyCurrency ||
      debouncedAmount <= 0
    ) {
      return undefined;
    }
    return {
      businessEntityId: selectedBusinessEntityId,
      operation: selectedOperation,
      amount: new Decimal(debouncedAmount),
      sellCurrency: selectedSellCurrency,
      buyCurrency: selectedBuyCurrency,
    };
  }, [
    isEburyBusinessEntity,
    selectedBusinessEntityId,
    selectedOperation,
    selectedSellCurrency,
    selectedBuyCurrency,
    debouncedAmount,
  ]);
  const {
    data: eburyValueDateOptionsData,
    loading: eburyValueDateOptionsLoading,
    error: eburyValueDateOptionsError,
  } = useQuery(
    graphql(`
      query AdminNewFXTradeEburyValueDateOptions(
        $input: EburyValueDateOptionsInput!
      ) {
        eburyValueDateOptions(input: $input) {
          dates {
            date
            available
            label
          }
        }
      }
    `),
    {
      variables: eburyValueDateOptionsInput
        ? { input: eburyValueDateOptionsInput }
        : undefined,
      skip: !eburyValueDateOptionsInput,
      fetchPolicy: "network-only",
      onCompleted: (data) => {
        const availableDates = data.eburyValueDateOptions.dates
          .filter((d) => d.available)
          .map((d) => d.date);
        const currentValueDate = DateTime.fromJSDate(
          newQuoteForm.getFieldValue("valueDate"),
        ).toISODate();
        // default to the earliest available date if the current selection is no longer available
        if (
          availableDates.length > 0 &&
          (!currentValueDate || !availableDates.includes(currentValueDate))
        ) {
          newQuoteForm.setFieldValue(
            "valueDate",
            DateTime.fromISO(availableDates[0]).toJSDate(),
          );
        }
      },
    },
  );
  const eburyValueDateOptions =
    eburyValueDateOptionsInput && !eburyValueDateOptionsLoading
      ? (eburyValueDateOptionsData?.eburyValueDateOptions.dates ?? [])
      : [];

  let eburyValueDatePlaceholder = "Select value date";
  if (!eburyValueDateOptionsInput) {
    eburyValueDatePlaceholder = "Select operation, amount and currencies first";
  } else if (eburyValueDateOptionsLoading) {
    eburyValueDatePlaceholder = "Loading value dates...";
  }

  let valueDateField: ReactNode;
  switch (selectedBusinessEntity?.fxProvider) {
    case "EBURY":
      valueDateField = (
        <newQuoteForm.AppField name="valueDate">
          {(field) => (
            <div className="flex w-full flex-col gap-2">
              <Label htmlFor={field.name}>Value date</Label>
              <Select
                name={field.name}
                value={
                  eburyValueDateOptions.length > 0
                    ? (DateTime.fromJSDate(field.state.value).toISODate() ??
                      undefined)
                    : undefined
                }
                onValueChange={(v) =>
                  field.handleChange(DateTime.fromISO(v).toJSDate())
                }
                disabled={eburyValueDateOptions.length === 0}
              >
                <SelectTrigger id={field.name} className="w-full">
                  <SelectValue placeholder={eburyValueDatePlaceholder} />
                </SelectTrigger>
                <SelectContent>
                  {eburyValueDateOptions.map((option) => (
                    <SelectItem
                      key={option.date}
                      value={option.date}
                      disabled={!option.available}
                    >
                      {DateTime.fromISO(option.date).toFormat("ccc d LLL yyyy")}
                      {option.label ? ` (${option.label})` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {eburyValueDateOptionsError && (
                <div className="text-danger text-sm">
                  {eburyValueDateOptionsError.message}
                </div>
              )}
            </div>
          )}
        </newQuoteForm.AppField>
      );
      break;
    case "CURRENCYCLOUD":
      valueDateField = selectedDatePreference === "CUSTOM_DATE" && (
        <newQuoteForm.AppField name="valueDate">
          {(field) => (
            <field.DatePickerField className="w-min" label="Value date" />
          )}
        </newQuoteForm.AppField>
      );
      break;
    default:
      valueDateField = (
        <newQuoteForm.AppField name="valueDate">
          {(field) => (
            <field.DatePickerField className="w-min" label="Value date" />
          )}
        </newQuoteForm.AppField>
      );
  }

  const currencyOptions =
    selectedOrganisation && selectedBusinessEntityId
      ? selectedOrganisation.businessEntities
          ?.find((be) => be.id === selectedBusinessEntityId)
          ?.assets.filter((asset) => asset.class === "FX")
          .map((asset) => asset.currency)
          ?.filter((currency): currency is Currency =>
            isCurrency(currency as string),
          )
      : undefined;
  const sellCurrencyOptions =
    selectedOperation === "BUY" && selectedBuyCurrency
      ? currencyOptions?.filter((currency) => currency !== selectedBuyCurrency)
      : currencyOptions;
  const buyCurrencyOptions =
    selectedOperation === "SELL" && selectedSellCurrency
      ? currencyOptions?.filter((currency) => currency !== selectedSellCurrency)
      : currencyOptions;

  const currencyPlaceholders = !selectedBusinessEntityId
    ? {
        buy: "Select business entity first",
        sell: "Select business entity first",
      }
    : {
        buy:
          (currencyOptions?.length ?? 0) > 0
            ? "Select buy currency"
            : "No FX accounts available",
        sell:
          (currencyOptions?.length ?? 0) > 0
            ? "Select sell currency"
            : "No FX accounts available",
      };

  const quote = quoteData?.createFXTradeQuote.quote;

  const quoteMatchesForm = useMemo(() => {
    if (!quote) {
      return false;
    }
    if (quote.buyAmount.currency !== selectedBuyCurrency) {
      return false;
    }
    if (quote.sellAmount.currency !== selectedSellCurrency) {
      return false;
    }

    switch (selectedOperation) {
      case "BUY":
        if (!quote.buyAmount.amount.eq(selectedAmount)) {
          return false;
        }
        break;
      case "SELL":
        if (!quote.sellAmount.amount.eq(selectedAmount)) {
          return false;
        }
        break;
      case undefined:
        return false;
      default:
        throw neverFunc(selectedOperation);
    }

    return true;
  }, [
    quote,
    selectedAmount,
    selectedBuyCurrency,
    selectedSellCurrency,
    selectedOperation,
  ]);

  const createTradeVars = useMemo(() => {
    if (!quote) {
      return;
    }
    return {
      options: {
        quoteId: quote.id,
      },
    };
  }, [quote]);

  return (
    <AdminPageContainer testId="admin-fx-trades-new">
      <AdminPageHeader
        title="New spot FX trade"
        subtitle="Quote and book an FX trade"
      />
      <newQuoteForm.AppForm>
        <form
          className="max-w-lg"
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            void newQuoteForm.handleSubmit();
          }}
        >
          <div className="flex flex-col gap-4">
            <div>
              <newQuoteForm.Field name="organisationId">
                {(field) => (
                  <>
                    <Label htmlFor={field.name} className="mb-1 block">
                      Organisation
                    </Label>
                    <Select
                      name={field.name}
                      onValueChange={(e) => {
                        field.handleChange(e);
                        field.form.resetField("businessEntityId");
                        field.form.resetField("sellCurrency");
                        field.form.resetField("buyCurrency");
                      }}
                    >
                      <SelectTrigger id={field.name} className="w-full">
                        <SelectValue placeholder="Select organisation" />
                      </SelectTrigger>
                      <SelectContent>
                        {[...filteredOrganisationOptions]
                          .sort((a, b) => a.name.localeCompare(b.name))
                          .map((org) => (
                            <SelectItem key={org.id} value={org.id}>
                              {org.name}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </>
                )}
              </newQuoteForm.Field>
            </div>

            <div>
              <newQuoteForm.Field name="businessEntityId">
                {(field) => (
                  <>
                    <Label htmlFor={field.name} className="mb-1 block">
                      Business Entity
                    </Label>
                    <Select
                      name={field.name}
                      onValueChange={(e) => {
                        field.handleChange(e);
                        field.form.resetField("sellCurrency");
                        field.form.resetField("buyCurrency");
                      }}
                      disabled={selectedOrganisation === undefined}
                    >
                      <SelectTrigger id={field.name} className="w-full">
                        <SelectValue
                          placeholder={
                            selectedOrganisation === undefined
                              ? "Select organisation first"
                              : "Select business entity"
                          }
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {[...businessEntityOptions]
                          .sort((a, b) => a.name.localeCompare(b.name))
                          .map((be) => (
                            <SelectItem key={be.id} value={be.id}>
                              <div className="flex gap-2">
                                <LabelPill
                                  label={prettyPrintEnum(be.fxProvider)}
                                  variant="primary"
                                ></LabelPill>
                                {be.name}
                              </div>
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </>
                )}
              </newQuoteForm.Field>
            </div>

            <div>
              <newQuoteForm.AppField name="operation">
                {(field) => (
                  <div className="flex flex-col gap-2">
                    <Label htmlFor={field.name}>Operation</Label>
                    <Select
                      name={field.name}
                      defaultValue={field.state.value}
                      onValueChange={field.handleChange}
                    >
                      <SelectTrigger id={field.name} className="w-full">
                        <SelectValue placeholder="Select operation" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="SELL">Sell</SelectItem>
                        <SelectItem value="BUY">Buy</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </newQuoteForm.AppField>
            </div>

            <div>
              <newQuoteForm.AppField name="amount">
                {(field) => (
                  <>
                    <Label htmlFor={field.name} className="mb-1 block">
                      Amount
                    </Label>
                    <Input
                      type="number"
                      id={field.name}
                      name={field.name}
                      step="0.01"
                      min="0.01"
                      className="w-full"
                      required
                      onChange={(e) =>
                        field.handleChange(Number(e.target.value))
                      }
                    />
                  </>
                )}
              </newQuoteForm.AppField>
            </div>

            <div className="flex gap-2">
              <newQuoteForm.Field name="sellCurrency">
                {(field) => (
                  <div className="flex w-1/2 flex-col gap-2">
                    <Label htmlFor={field.name} className="mb-1 block">
                      Sell Currency
                    </Label>
                    <Select
                      name={field.name}
                      defaultValue={field.state.value}
                      onValueChange={field.handleChange}
                      disabled={(sellCurrencyOptions?.length ?? 0) === 0}
                    >
                      <SelectTrigger id={field.name} className="w-full">
                        <SelectValue placeholder={currencyPlaceholders.sell} />
                      </SelectTrigger>
                      <SelectContent>
                        {sellCurrencyOptions?.map((currency) => {
                          return (
                            <SelectItem key={currency} value={currency}>
                              {currency}
                            </SelectItem>
                          );
                        }) ?? null}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </newQuoteForm.Field>
              <newQuoteForm.Field name="buyCurrency">
                {(field) => (
                  <div className="flex w-1/2 flex-col gap-2">
                    <Label htmlFor={field.name} className="mb-1 block">
                      Buy Currency
                    </Label>
                    <Select
                      name={field.name}
                      defaultValue={field.state.value}
                      onValueChange={field.handleChange}
                      disabled={(buyCurrencyOptions?.length ?? 0) === 0}
                    >
                      <SelectTrigger id={field.name} className="w-full">
                        <SelectValue placeholder={currencyPlaceholders.buy} />
                      </SelectTrigger>
                      <SelectContent>
                        {buyCurrencyOptions?.map((currency) => {
                          return (
                            <SelectItem key={currency} value={currency}>
                              {currency}
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </newQuoteForm.Field>
            </div>

            <div className="flex gap-4">
              {selectedBusinessEntity?.fxProvider === "CURRENCYCLOUD" && (
                <newQuoteForm.AppField name="datePreference">
                  {(field) => (
                    <div className="w-full">
                      <field.SelectField
                        label="Date preference"
                        triggerClassName="w-full"
                      >
                        <field.SelectItem value="EARLIEST">
                          Earliest
                        </field.SelectItem>
                        <field.SelectItem value="CUSTOM_DATE">
                          Choose date
                        </field.SelectItem>
                        <field.SelectItem value="DEFAULT">
                          Default (T+2)
                        </field.SelectItem>
                        <field.SelectItem value="NEXT_DAY">
                          Next day (T+1)
                        </field.SelectItem>
                        <field.SelectItem value="OPTIMIZE_LIQUIDITY">
                          Optimise liquidity
                        </field.SelectItem>
                      </field.SelectField>
                    </div>
                  )}
                </newQuoteForm.AppField>
              )}

              {valueDateField}
            </div>

            {quoteLoading && (
              <div className="flex gap-2">
                <Spinner size="md" />
                Creating quote
              </div>
            )}

            {!quoteLoading && quote && quoteMatchesForm && (
              <div>
                <div className="flex items-center">
                  <div className="flex w-1/3 flex-col gap-2">
                    {quote?.sellAmount ? (
                      <NumberComponent
                        format={NumberComponent.format.currency(
                          quote.sellAmount.currency,
                        )}
                        value={quote.sellAmount.amount}
                      />
                    ) : null}
                  </div>

                  <div className="flex w-1/3 flex-col items-center gap-2">
                    <NumberComponent
                      format={NumberComponent.format.decimal({ digits: 6 })}
                      value={quote.sellToBuyRate}
                    />
                    <NumberComponent
                      format={NumberComponent.format.decimal()}
                      value={quote.buyToSellRate}
                    />
                  </div>

                  <div className="flex w-1/3 flex-col items-end gap-2">
                    {quote?.buyAmount ? (
                      <NumberComponent
                        format={NumberComponent.format.currency(
                          quote.buyAmount.currency,
                        )}
                        value={quote.buyAmount.amount}
                      />
                    ) : null}
                  </div>
                </div>

                <div className="mt-2 flex flex-col gap-2 pt-2">
                  <div className="flex items-center gap-2 text-secondary text-sm">
                    <div>Value Date: {quote.valueDate}</div>
                  </div>

                  {quote.feeAmount && (
                    <>
                      <div className="flex items-center gap-1 text-secondary text-sm">
                        <div>Fee amount:</div>
                        <CurrencyAmountUI
                          currency={quote.feeAmount.currency}
                          amount={quote.feeAmount.amount}
                        />
                      </div>
                      <div className="flex items-center gap-1 text-secondary text-sm">
                        <div>FX fee margin bps:</div>
                        {selectedOrganisation?.fxMarginRateBps.toFixed()}
                      </div>
                    </>
                  )}
                  {quote.externalId && quote && (
                    <div className="mt-1 flex items-center gap-2 text-xs">
                      <div>Ebury quote ID:</div>
                      <span className="font-mono">{quote.externalId}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="mt-4 flex w-full justify-end gap-2">
              <newQuoteForm.SubmitButton
                childrenWhileSubmitting="Creating quote..."
                disabled={quoteLoading || tradeLoading}
              >
                {quote && quoteMatchesForm ? "Refresh" : "Create"} quote
              </newQuoteForm.SubmitButton>
            </div>

            {quote && quoteMatchesForm && (
              <div className="flex flex-col gap-4">
                <Alert
                  kind="warning"
                  title="Placing trade"
                  description={(() => {
                    if (selectedBusinessEntity?.fxProvider === "EBURY") {
                      return `If you click "Accept final rate" you will enter into a legal contract with Ebury to buy or sell the currency you have selected at the rate specified above.`;
                    }
                    if (
                      selectedBusinessEntity?.fxProvider === "CURRENCYCLOUD"
                    ) {
                      return `If you click "Place trade" you will enter into a legal contract with Visa to buy or sell the currency you have selected. The actual rate will be determined at time of execution.`;
                    }
                    return "-";
                  })()}
                />
                <Button
                  disabled={quoteLoading || tradeLoading || !createTradeVars}
                  onClick={(event) => {
                    event.preventDefault();
                    if (quote) {
                      createFXTradeMutation({
                        variables: {
                          input: {
                            quoteId: quote.id,
                          },
                        },
                      });
                    }
                  }}
                >
                  {selectedBusinessEntity?.fxProvider === "EBURY" &&
                    "Accept final rate"}
                  {selectedBusinessEntity?.fxProvider === "CURRENCYCLOUD" &&
                    "Place trade"}
                </Button>
              </div>
            )}
          </div>
        </form>
      </newQuoteForm.AppForm>
    </AdminPageContainer>
  );
}
